import { PROCESSING_TYPE } from '../constants/processing';
import { z } from 'zod';
import { RECORD_TYPE } from '../constants/catalog';
import { CONSENT_ARTEFACT, PROCESSING_PURPOSE } from '../constants/dpdp';
import { RECORD_STATUS, type RecordStatus } from '../constants/statuses';
import { emailSchema, optionalTrimmedString, phoneSchema, trimmedString } from './common';

/**
 * How far through its own lifecycle the legacy system has taken an application.
 *
 * Deliberately a short, stable vocabulary rather than the legacy status column
 * verbatim: that column stays at `paid` for the whole of fulfilment and so
 * cannot say whether a certificate has been issued or a parcel has shipped.
 */
/**
 * Stands in for a record title the website has not captured yet.
 *
 * Deliberately reads as a gap rather than as content: an operator scanning the
 * list should see immediately that this record is incomplete, not mistake a
 * plausible-looking filler for the applicant's own words.
 */
export const UNTITLED_RECORD_TITLE = '(Untitled — awaiting details)';

export const LEGACY_STAGE = {
  // Started on the website but never submitted. Mirrored so the sales team can
  // see and chase an abandoned application; it is a lead, not a case.
  DRAFT: 'draft',
  // Before any decision. An application is mirrored from the moment it is
  // filed, so the evaluation itself can be run from the CRM.
  SUBMITTED: 'submitted',
  UNDER_REVIEW: 'under_review',
  VALIDATED: 'validated',
  /**
   * DEV-006. Selected, then withdrawn from active processing for missing the
   * 48-hour payment window. Reversible — the website can reactivate it.
   */
  ON_HOLD: 'on_hold',
  // Decided, and everything after.
  APPROVED: 'approved',
  PAYMENT_RECEIVED: 'payment_received',
  CERTIFICATE_ISSUED: 'certificate_issued',
  DISPATCH_PENDING: 'dispatch_pending',
  DISPATCHED: 'dispatched',
  DELIVERED: 'delivered',
  // Terminal decisions, reachable from anywhere on the ladder.
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
} as const;

export type LegacyStage = (typeof LEGACY_STAGE)[keyof typeof LEGACY_STAGE];

/**
 * Stages that are a *decision* rather than a step along the ladder.
 *
 * These bypass the "only advance" rule below: rejecting an application that had
 * already been approved is a real thing the website can do, and a rank
 * comparison would silently discard it.
 */
export const LEGACY_DECISION_STAGES: readonly LegacyStage[] = [
  LEGACY_STAGE.REJECTED,
  LEGACY_STAGE.CANCELLED,
  /**
   * DEV-006. On Hold is a decision taken about a record, not a step along the
   * ladder — it can be applied from anywhere, including after selection, and
   * must be allowed to land. Without this the only-advance guard compares its
   * rank (off the ladder, so -1) against wherever the record currently sits and
   * silently refuses every withdrawal.
   */
  LEGACY_STAGE.ON_HOLD,
];

export function isLegacyDecisionStage(stage: LegacyStage): boolean {
  return LEGACY_DECISION_STAGES.includes(stage);
}

/**
 * ── Review actions on a mirrored application ─────────────────────────────────
 *
 * The six decisions the website's admin panel can take on an application, now
 * reachable from here for any record that came from there.
 *
 * The decision is applied *on the website*, not locally. It owns the applicant
 * relationship — their portal login, the address they applied with, the WhatsApp
 * thread, the invoice templates — so routing the decision through it is what
 * makes an approval taken here indistinguishable, to the applicant, from one
 * taken there. The mirrored snapshot then comes back and moves the record.
 */
export const LEGACY_APPLICATION_ACTION = {
  APPROVE: 'approve',
  REJECT: 'reject',
  REQUEST_INFO: 'request_info',
  VALIDATE: 'validate',
  CANCEL: 'cancel',
  REOPEN: 'reopen',
  /** DEV-006. Withdraw a selected claim from active processing. */
  HOLD: 'hold',
  /** DEV-006. Put an On Hold claim back into active processing. */
  REACTIVATE: 'reactivate',
} as const;

export type LegacyApplicationAction =
  (typeof LEGACY_APPLICATION_ACTION)[keyof typeof LEGACY_APPLICATION_ACTION];

export const LEGACY_APPLICATION_ACTION_META: Readonly<
  Record<
    LegacyApplicationAction,
    {
      label: string;
      /** What the applicant is told, so the operator knows before clicking. */
      effect: string;
      /** Which free-text field the action requires, if any. */
      field: 'note' | 'message' | 'reason' | null;
      required: boolean;
      tone: 'primary' | 'danger' | 'warning' | 'secondary';
    }
  >
> = {
  [LEGACY_APPLICATION_ACTION.APPROVE]: {
    label: 'Approve',
    effect: 'Emails and WhatsApps the applicant that they are approved, with the payment deadline.',
    field: 'note',
    required: true,
    tone: 'primary',
  },
  [LEGACY_APPLICATION_ACTION.REJECT]: {
    label: 'Reject',
    effect: 'Emails and WhatsApps the applicant that the application was not accepted, quoting your reason.',
    field: 'note',
    required: true,
    tone: 'danger',
  },
  [LEGACY_APPLICATION_ACTION.REQUEST_INFO]: {
    label: 'Request information',
    effect: 'Emails the applicant asking for what you type below, and moves the application to Under Review.',
    field: 'message',
    required: true,
    tone: 'warning',
  },
  [LEGACY_APPLICATION_ACTION.VALIDATE]: {
    label: 'Mark verified',
    effect: 'Emails the applicant that their documents passed checks and the panel is deciding.',
    field: 'note',
    required: true,
    tone: 'secondary',
  },
  [LEGACY_APPLICATION_ACTION.HOLD]: {
    label: 'Place on hold',
    effect:
      'Withdraws the selected claim from active processing, unpublishes the awardee page and record, and tells the applicant it is On Hold rather than cancelled. Reversible.',
    field: 'reason',
    required: true,
    tone: 'warning',
  },
  [LEGACY_APPLICATION_ACTION.REACTIVATE]: {
    label: 'Reactivate',
    effect:
      'Returns an On Hold claim to active processing with a fresh payment window, and tells the applicant. An applicant who has already paid returns as paid.',
    field: 'note',
    required: true,
    tone: 'primary',
  },
  [LEGACY_APPLICATION_ACTION.CANCEL]: {
    label: 'Cancel',
    effect: 'Emails the applicant with your reason, and unpublishes their awardee page and record.',
    field: 'reason',
    required: true,
    tone: 'danger',
  },
  [LEGACY_APPLICATION_ACTION.REOPEN]: {
    label: 'Reopen',
    effect: 'Puts a cancelled or rejected application back into the workflow where it left off.',
    field: 'note',
    required: true,
    tone: 'secondary',
  },
};

export const legacyApplicationActionSchema = z.object({
  action: z.nativeEnum(LEGACY_APPLICATION_ACTION),
  note: optionalTrimmedString(2000),
  message: optionalTrimmedString(2000),
  reason: optionalTrimmedString(2000),
  deadlineHours: z.coerce.number().int().min(1).max(720).optional(),
});

export type LegacyApplicationActionInput = z.infer<typeof legacyApplicationActionSchema>;

/**
 * ── The website's own pipeline, verbatim ─────────────────────────────────────
 *
 * The stage names and step labels the NBR website's admin panel shows, so an
 * operator moving between the two systems reads the same words for the same
 * thing. The CRM keeps its own richer status vocabulary underneath — it drives
 * queues, reports and permissions — but where an application sits *in the
 * website's terms* is what the two systems actually agree on, and that is what
 * this describes.
 *
 * Deliberately a display concern only. Nothing here decides what is allowed;
 * `legacyActionsFor` on the server is the authority, and it mirrors the
 * website's own route guards.
 */
export const LEGACY_PIPELINE_STAGE = {
  SUBMITTED: 'submitted',
  UNDER_REVIEW: 'under_review',
  APPROVED: 'approved',
  PAYMENT_RECEIVED: 'payment_received',
  DISPATCHED: 'dispatched',
} as const;

export type LegacyPipelineStage =
  (typeof LEGACY_PIPELINE_STAGE)[keyof typeof LEGACY_PIPELINE_STAGE];

/** The five steps the website's status rail shows, in order. */
export const LEGACY_PIPELINE: ReadonlyArray<{
  stage: LegacyPipelineStage;
  label: string;
  /** CRM statuses that mean the application has reached this step. */
  statuses: readonly RecordStatus[];
}> = [
  {
    stage: LEGACY_PIPELINE_STAGE.SUBMITTED,
    label: 'Submitted',
    statuses: [RECORD_STATUS.APPLICATION_SUBMITTED],
  },
  {
    stage: LEGACY_PIPELINE_STAGE.UNDER_REVIEW,
    label: 'Under Review',
    statuses: [RECORD_STATUS.UNDER_REVIEW, RECORD_STATUS.VERIFICATION_PENDING],
  },
  {
    stage: LEGACY_PIPELINE_STAGE.APPROVED,
    label: 'Approved',
    statuses: [RECORD_STATUS.SELECTED, RECORD_STATUS.PAYMENT_PENDING],
  },
  {
    stage: LEGACY_PIPELINE_STAGE.PAYMENT_RECEIVED,
    label: 'Payment Received',
    statuses: [
      RECORD_STATUS.PAYMENT_RECEIVED,
      RECORD_STATUS.CERTIFICATE_PENDING,
      RECORD_STATUS.CERTIFICATE_UPLOADED,
      RECORD_STATUS.DISPATCH_PENDING,
    ],
  },
  {
    stage: LEGACY_PIPELINE_STAGE.DISPATCHED,
    label: 'Package Dispatched',
    statuses: [
      RECORD_STATUS.DISPATCHED,
      RECORD_STATUS.DELIVERED,
      // Publication follows delivery in the CRM's own pipeline. The website
      // has no stage past "dispatched", so on its rail a record being written
      // up for the magazine still reads as dispatched — which is true, and is
      // the furthest its five steps can express.
      RECORD_STATUS.PUBLICATION,
      RECORD_STATUS.COMPLETED,
    ],
  },
];

/** How far along the website's rail a CRM status sits. -1 when off it entirely. */
export function legacyPipelineIndex(status: RecordStatus): number {
  return LEGACY_PIPELINE.findIndex((step) => step.statuses.includes(status));
}

/**
 * Where each legacy stage lands in the CRM's own workflow.
 *
 * `approved` maps to Selected rather than Payment Pending because Payment
 * Pending presupposes a payment plan, and the legacy system raises its own
 * invoice without telling us a package. Selected is the honest equivalent of
 * "approved, awaiting money".
 */
export const LEGACY_STAGE_TO_STATUS: Readonly<Record<LegacyStage, RecordStatus>> = {
  // An unsubmitted draft is exactly what New Lead is for — someone who has
  // shown intent and has not yet given the back office anything to act on.
  [LEGACY_STAGE.DRAFT]: RECORD_STATUS.NEW_LEAD,
  [LEGACY_STAGE.SUBMITTED]: RECORD_STATUS.APPLICATION_SUBMITTED,
  [LEGACY_STAGE.UNDER_REVIEW]: RECORD_STATUS.UNDER_REVIEW,
  // The website's `validated` means the back-office team has checked the
  // application and it is waiting on the approve/reject decision. Verification
  // Pending is the status that occupies that same slot here — after review,
  // before Selected — even though its label reads from the other direction.
  [LEGACY_STAGE.VALIDATED]: RECORD_STATUS.VERIFICATION_PENDING,
  [LEGACY_STAGE.ON_HOLD]: RECORD_STATUS.ON_HOLD,
  [LEGACY_STAGE.APPROVED]: RECORD_STATUS.SELECTED,
  [LEGACY_STAGE.PAYMENT_RECEIVED]: RECORD_STATUS.PAYMENT_RECEIVED,
  [LEGACY_STAGE.CERTIFICATE_ISSUED]: RECORD_STATUS.CERTIFICATE_UPLOADED,
  [LEGACY_STAGE.DISPATCH_PENDING]: RECORD_STATUS.DISPATCH_PENDING,
  [LEGACY_STAGE.DISPATCHED]: RECORD_STATUS.DISPATCHED,
  [LEGACY_STAGE.DELIVERED]: RECORD_STATUS.DELIVERED,
  [LEGACY_STAGE.REJECTED]: RECORD_STATUS.REJECTED,
  // The website cancels an application that was approved or paid and then
  // abandoned. Closed is this system's terminal-without-completion status.
  [LEGACY_STAGE.CANCELLED]: RECORD_STATUS.CLOSED,
};

/**
 * Rank used to decide whether an incoming snapshot moves a record forward.
 *
 * A snapshot that describes an *earlier* stage than the CRM already holds is
 * a late or replayed delivery; its lifecycle blocks are still merged, but the
 * record's status is left where it is rather than being dragged backwards.
 */
export const LEGACY_STAGE_RANK: Readonly<Record<LegacyStage, number>> = {
  [LEGACY_STAGE.DRAFT]: -0.5,
  [LEGACY_STAGE.SUBMITTED]: 0,
  [LEGACY_STAGE.UNDER_REVIEW]: 1,
  [LEGACY_STAGE.VALIDATED]: 2,
  // Off the ladder like the other reversible-but-not-progress states: being put
  // on hold is not a step forward, and ranking it as one would let it block a
  // later, genuine advance under the only-advance rule.
  [LEGACY_STAGE.ON_HOLD]: -1,
  [LEGACY_STAGE.APPROVED]: 3,
  [LEGACY_STAGE.PAYMENT_RECEIVED]: 4,
  [LEGACY_STAGE.CERTIFICATE_ISSUED]: 5,
  [LEGACY_STAGE.DISPATCH_PENDING]: 6,
  [LEGACY_STAGE.DISPATCHED]: 7,
  [LEGACY_STAGE.DELIVERED]: 8,
  // Off the ladder. Never compared — `isLegacyDecisionStage` short-circuits the
  // guard first — but every stage needs a rank for the lookup to stay total.
  [LEGACY_STAGE.REJECTED]: -1,
  [LEGACY_STAGE.CANCELLED]: -1,
};

/**
 * Inbound webhook from the existing NBR website admin panel (P2-14).
 *
 * When an application is approved over there, it POSTs here and the CRM
 * creates or merges the master applicant and opens a new record with source
 * "Website" — zero re-typing. The payload is deliberately loose about optional
 * fields (the legacy system may not have all of them) but strict about the
 * identifiers we need for the merge decision.
 *
 * Transport security: HMAC-SHA256 over the raw body with a shared secret,
 * sent as `X-NBR-Signature: t=<unix>,v1=<hex>`. The timestamp is inside the
 * signed payload so a captured request can't be replayed later, and
 * `externalId` makes retries idempotent.
 */
export const nbrWebhookApplicationSchema = z.object({
  /** The legacy system's own primary key. Idempotency key for this import. */
  externalId: trimmedString(120),
  externalUrl: z.string().url().max(1000).optional(),
  approvedAt: z.coerce.date().optional(),
  /**
   * When the application last changed on the website.
   *
   * Folded into the event key the CRM deduplicates on. Without it a state the
   * application *returns* to cannot be told apart from a retry of the first
   * time it held that state — reopening a cancelled application back to
   * `approved` sends a snapshot byte-identical to the original approval, and it
   * was silently dropped as a duplicate.
   */
  sourceUpdatedAt: z.coerce.date().optional(),

  /**
   * DEV-001. The turnaround the applicant chose on the website form.
   *
   * Optional, and defaulted rather than nullable: every application filed
   * before the field existed was worked through on the standard timeline, so
   * that is the true answer for them — and it saves every screen here deciding
   * for itself what an absent value means.
   */
  processingType: z.nativeEnum(PROCESSING_TYPE).default(PROCESSING_TYPE.STANDARD),

  /**
   * DEV-002. Whether the applicant asked for an adjudicator to attend.
   *
   * Carries a ₹1,00,000 fee on the website, so it explains a large part of what
   * the applicant paid — and it means someone has to be scheduled to attend.
   * Defaulted to false: an application filed before the question existed did
   * not request one.
   */
  adjudicatorRequested: z.coerce.boolean().default(false),

  /**
   * Whether the website is collecting the adjudicator fee from the applicant.
   *
   * Separate from the request: staff may waive, defer, or settle it outside the
   * portal, and none of those cancel the adjudicator. Defaults true so an
   * application from before the switch existed reads as it always did.
   */
  adjudicatorFeeDue: z.coerce.boolean().default(true),

  applicant: z.object({
    fullName: trimmedString(150),
    fatherName: optionalTrimmedString(150),
    motherName: optionalTrimmedString(150),
    dateOfBirth: z.coerce.date().optional(),
    gender: optionalTrimmedString(20),
    mobile: trimmedString(20),
    whatsapp: phoneSchema.optional(),
    /**
     * Absent or blank is accepted, and normalised to an empty string.
     *
     * A strict `emailSchema` here rejected the entire snapshot with "Enter a
     * valid email address" for anyone who applied by phone and never gave one —
     * so those applicants were invisible in this system rather than merely
     * un-emailable. An address that is present must still be well-formed;
     * duplicate matching probes only truthy values, so blanks never collapse
     * two people into one.
     */
    email: z
      .union([z.literal(''), emailSchema])
      .optional()
      .transform((value) => value ?? ''),
    addressLine: optionalTrimmedString(300),
    city: optionalTrimmedString(100),
    state: optionalTrimmedString(100),
    country: optionalTrimmedString(100),
    pincode: optionalTrimmedString(12),
    nationality: optionalTrimmedString(100),
  }),

  achievement: z.object({
    /**
     * No length cap, deliberately.
     *
     * A cap here rejects the whole snapshot over the tail of a display string,
     * and the truncation that used to compensate silently altered a title that
     * gets printed on a certificate. The column behind it is unbounded.
     *
     * Nor is a title required. Applications are mirrored from the moment they
     * are filed, and at that point the title is often still blank — rejecting
     * them left the earliest stages of the pipeline, the ones this system most
     * needs to show, permanently unsynced. A blank arrives as a visible
     * placeholder that the real title overwrites on the next push.
     */
    recordTitle: z
      .string()
      .trim()
      .optional()
      .transform((value) => value || UNTITLED_RECORD_TITLE),
    /** Free text from the legacy system; mapped to a category by name, falling
     *  back to "Other" and flagging the record for review. */
    category: optionalTrimmedString(150),
    recordType: z.nativeEnum(RECORD_TYPE).default(RECORD_TYPE.INDIVIDUAL),
    description: z.string().trim().optional(),
    achievementDate: z.coerce.date().optional(),
    location: optionalTrimmedString(250),
    participantCount: z.coerce.number().int().min(1).default(1),
  }),

  /**
   * Files already collected by the website. The importer copies each one into
   * the vault rather than hot-linking, so the evidence survives the legacy
   * system being retired.
   */
  evidence: z
    .array(
      z.object({
        url: z.string().url().max(2000),
        fileName: optionalTrimmedString(255),
        kind: optionalTrimmedString(40),
        contentType: optionalTrimmedString(120),
      }),
    )
    .max(50)
    .default([]),

  /**
   * Consent the applicant gave on the website. Carried across so the DPDP
   * ledger reflects the real acceptance timestamp, not the import time.
   */
  consent: z
    .object({
      acceptedAt: z.coerce.date(),
      noticeVersion: optionalTrimmedString(20),
      purposes: z.array(z.nativeEnum(PROCESSING_PURPOSE)).default([]),
      artefacts: z.array(z.nativeEnum(CONSENT_ARTEFACT)).default([]),
      ipAddress: optionalTrimmedString(45),
      userAgent: optionalTrimmedString(500),
    })
    .optional(),

  /**
   * ── Lifecycle mirror ───────────────────────────────────────────────────────
   *
   * The legacy system sends a *full snapshot* on every event rather than a
   * delta, so the four blocks below describe where the application actually
   * stands right now. Two consequences worth stating:
   *
   *  • A push that gets lost costs nothing — the next one carries the same
   *    state plus whatever changed since.
   *  • Replaying an old event cannot roll the CRM backwards, because the
   *    importer compares the snapshot to what it already holds.
   *
   * All optional: an installation that only ever sends approvals keeps working
   * exactly as it did before these fields existed.
   */
  stage: z.nativeEnum(LEGACY_STAGE).optional(),

  payment: z
    .object({
      externalId: optionalTrimmedString(120),
      plan: optionalTrimmedString(60),
      /** Rupees as a decimal string — the CRM never holds money as a float. */
      amount: z.string().regex(/^\d+(\.\d{1,2})?$/).optional(),
      amountPaise: z.coerce.number().int().nonnegative().optional(),
      currency: optionalTrimmedString(3),
      status: optionalTrimmedString(20),
      method: optionalTrimmedString(40).nullable().optional(),
      referenceNumber: optionalTrimmedString(120).nullable().optional(),
      invoiceUrl: z.string().url().max(1000).nullable().optional(),
      notes: optionalTrimmedString(2000).nullable().optional(),
      paidAt: z.coerce.date().nullable().optional(),
      deadline: z.coerce.date().nullable().optional(),
    })
    .nullable()
    .optional(),

  certificate: z
    .object({
      certificateId: trimmedString(80),
      holderName: optionalTrimmedString(150),
      recordTitle: z.string().trim().optional(),
      category: optionalTrimmedString(120),
      issuedAt: z.coerce.date().optional(),
      revoked: z.boolean().default(false),
      revokeReason: optionalTrimmedString(500).nullable().optional(),
      /** Public verification page on the customer site. The CRM links to it
       *  rather than hosting a second copy of the certificate. */
      verificationUrl: z.string().url().max(1000).optional(),
    })
    .nullable()
    .optional(),

  dispatch: z
    .object({
      externalId: optionalTrimmedString(120),
      status: optionalTrimmedString(30),
      courierName: optionalTrimmedString(150).nullable().optional(),
      trackingNumber: optionalTrimmedString(150).nullable().optional(),
      trackingUrl: z.string().url().max(1000).nullable().optional(),
      address: z.record(z.unknown()).nullable().optional(),
      notes: optionalTrimmedString(2000).nullable().optional(),
      dispatchedAt: z.coerce.date().nullable().optional(),
      deliveredAt: z.coerce.date().nullable().optional(),
    })
    .nullable()
    .optional(),

  /**
   * Awardee page on the customer site. Read-only in the CRM: those pages are
   * created and published on the public site, and this is the link across.
   */
  awardee: z
    .object({
      slug: trimmedString(200),
      isPublished: z.boolean().default(false),
      coverImageUrl: z.string().url().max(1000).nullable().optional(),
      publicUrl: z.string().url().max(1000).nullable().optional(),
    })
    .nullable()
    .optional(),

  /**
   * The award being applied for — "Bharat Vibhushan" and the like.
   *
   * A first-class block rather than a line in `extra` because the record view
   * badges it, and because `extra` is excluded from the snapshot hash: a
   * correction to the award would otherwise not register as a real change.
   */
  award: z
    .object({
      title: trimmedString(200),
      slug: optionalTrimmedString(250),
      category: optionalTrimmedString(150),
    })
    .nullish(),

  /** Anything the legacy system wants to hand over verbatim. Stored as JSONB. */
  extra: z.record(z.unknown()).optional(),
});

export type NbrWebhookApplication = z.infer<typeof nbrWebhookApplicationSchema>;

export const INTEGRATION_IMPORT_STATUS = {
  RECEIVED: 'received',
  PROCESSING: 'processing',
  IMPORTED: 'imported',
  MERGED: 'merged',
  DUPLICATE_SKIPPED: 'duplicate_skipped',
  BLOCKED_BLACKLIST: 'blocked_blacklist',
  FAILED: 'failed',
} as const;

export type IntegrationImportStatus =
  (typeof INTEGRATION_IMPORT_STATUS)[keyof typeof INTEGRATION_IMPORT_STATUS];

export interface IntegrationSyncStatus {
  readonly lastWebhookAt: string | null;
  readonly lastPollAt: string | null;
  readonly pending: number;
  readonly importedToday: number;
  readonly failedToday: number;
  readonly pollingEnabled: boolean;
  readonly recentFailures: ReadonlyArray<{
    readonly externalId: string;
    readonly error: string;
    readonly at: string;
  }>;
}

/** Header carrying the HMAC signature on inbound webhook requests. */
export const WEBHOOK_SIGNATURE_HEADER = 'x-nbr-signature';
export const WEBHOOK_IDEMPOTENCY_HEADER = 'x-nbr-idempotency-key';

/**
 * ── Blacklist, across the two systems ────────────────────────────────────────
 *
 * The CRM keeps the register — kind, reason, supporting documents, who lifted
 * it and why — because that is the record that has to survive being challenged
 * years later. The website keeps the *effect*: `users.is_blocked`, which its
 * login and its admin intake already honour.
 *
 * So the two halves are deliberately asymmetric. A blacklist added here sends
 * the website "block this person"; the website blocking someone from its own
 * Users screen sends back "they are blocked", and the CRM opens a register
 * entry for it so the reason is not lost the moment it crosses over.
 *
 * Identity travels as email and mobile rather than an id, because there is no
 * shared key for a *person* — `legacy_mirror` links applications, and a
 * blacklisted applicant may have none, or several belonging to different
 * website accounts.
 */
export const LEGACY_BLACKLIST_ACTION = {
  ADD: 'add',
  LIFT: 'lift',
} as const;

export type LegacyBlacklistAction =
  (typeof LEGACY_BLACKLIST_ACTION)[keyof typeof LEGACY_BLACKLIST_ACTION];

/**
 * What the website sends when an operator blocks or unblocks a user account.
 *
 * `userId` is the website's own primary key. It is carried for the audit trail
 * and for the reverse lookup on a later unblock; the CRM matches on the
 * identifiers, since a person here may predate the website account entirely.
 */
export const legacyUserBlockSchema = z.object({
  action: z.enum(['block', 'unblock']),
  userId: trimmedString(120),
  email: optionalTrimmedString(320),
  phone: optionalTrimmedString(40),
  fullName: optionalTrimmedString(200),
  /** Free text the operator typed on the website, if its screen collected any. */
  reason: optionalTrimmedString(2000),
});

export type LegacyUserBlockInput = z.infer<typeof legacyUserBlockSchema>;
